import {
  ApiResponse,
  ServerSummary,
  ServerDetail,
  PlayerInfo,
  FileEntry,
  KcdServerTomlConfig,
  ConsoleLogMessage,
  ServerResourceMetrics,
  AuditLogEntry,
  DatabaseTableSummary,
  DatabaseQueryResult,
  DatabaseInstance,
  CreatedDatabaseCredentials,
  BackupInfo,
} from '@kcdash/shared';

const API_BASE = '/api/v1';

class ApiClient {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('kcdash_token');
  }

  public setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('kcdash_token', token);
    } else {
      localStorage.removeItem('kcdash_token');
    }
  }

  public getToken(): string | null {
    return this.token;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    if (options.body) {
      headers['Content-Type'] = 'application/json';
    }

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include', // sends httpOnly cookies
    });

    // Check 401 Unauthorized
    if (response.status === 401) {
      this.setToken(null);
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }

    let json: any = null;
    try {
      json = await response.json();
    } catch {
      // Non-JSON response
    }

    if (!response.ok || (json && json.success === false)) {
      const errorMsg =
        (typeof json?.error === 'object' && json?.error?.message ? json.error.message : null) ||
        (typeof json?.error === 'string' ? json.error : null) ||
        json?.message ||
        `HTTP error ${response.status}`;
      throw new Error(errorMsg);
    }

    return (json?.data !== undefined ? json.data : json) as T;
  }

  // Auth
  public async login(usernameOrEmail: string, password: string) {
    const data = await this.request<{ token: string; user: any }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ usernameOrEmail, password }),
    });
    this.setToken(data.token);
    return data;
  }

  public async logout() {
    try {
      await this.request<void>('/auth/logout', { method: 'POST' });
    } finally {
      this.setToken(null);
    }
  }

  public async getMe() {
    return this.request<any>('/auth/me');
  }

  // Servers
  public async getServers(): Promise<ServerSummary[]> {
    return this.request<ServerSummary[]>('/servers');
  }

  public async getServer(id: string): Promise<ServerDetail> {
    return this.request<ServerDetail>(`/servers/${id}`);
  }

  public async createServer(data: any): Promise<ServerSummary> {
    return this.request<ServerSummary>('/servers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  public async updateServer(id: string, data: any): Promise<any> {
    return this.request(`/servers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  public async deleteServer(id: string): Promise<void> {
    return this.request(`/servers/${id}`, { method: 'DELETE' });
  }

  public async startServer(id: string): Promise<any> {
    return this.request(`/servers/${id}/start`, { method: 'POST', body: JSON.stringify({}) });
  }

  public async stopServer(id: string): Promise<any> {
    return this.request(`/servers/${id}/stop`, { method: 'POST', body: JSON.stringify({}) });
  }

  public async restartServer(id: string): Promise<any> {
    return this.request(`/servers/${id}/restart`, { method: 'POST', body: JSON.stringify({}) });
  }

  public async forceStopServer(id: string): Promise<any> {
    return this.request(`/servers/${id}/force-stop`, { method: 'POST', body: JSON.stringify({}) });
  }

  public async getMetrics(id: string, range: string = '1h'): Promise<ServerResourceMetrics[]> {
    return this.request<ServerResourceMetrics[]>(`/servers/${id}/metrics?range=${range}`);
  }

  // Console
  public async getLogs(id: string, limit: number = 200): Promise<ConsoleLogMessage[]> {
    return this.request<ConsoleLogMessage[]>(`/servers/${id}/logs?limit=${limit}`);
  }

  public async sendCommand(id: string, command: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/servers/${id}/command`, {
      method: 'POST',
      body: JSON.stringify({ command }),
    });
  }

  // Files
  public async listFiles(id: string, path: string = '/'): Promise<FileEntry[]> {
    return this.request<FileEntry[]>(`/servers/${id}/files?path=${encodeURIComponent(path)}`);
  }

  public async readFile(id: string, path: string): Promise<{ path: string; content: string }> {
    return this.request(`/servers/${id}/files/content?path=${encodeURIComponent(path)}`);
  }

  public async writeFile(id: string, path: string, content: string): Promise<void> {
    return this.request(`/servers/${id}/files/content`, {
      method: 'PUT',
      body: JSON.stringify({ path, content }),
    });
  }

  public async createEntry(id: string, path: string, isDirectory: boolean): Promise<void> {
    return this.request(`/servers/${id}/files/entry`, {
      method: 'POST',
      body: JSON.stringify({ path, isDirectory }),
    });
  }

  public async renameEntry(id: string, oldPath: string, newPath: string): Promise<void> {
    return this.request(`/servers/${id}/files/rename`, {
      method: 'POST',
      body: JSON.stringify({ oldPath, newPath }),
    });
  }

  public async deleteFiles(id: string, paths: string[]): Promise<void> {
    return this.request(`/servers/${id}/files`, {
      method: 'DELETE',
      body: JSON.stringify({ paths }),
    });
  }

  public async zipFiles(id: string, targetZipPath: string, sourcePaths: string[]): Promise<any> {
    return this.request(`/servers/${id}/files/zip`, {
      method: 'POST',
      body: JSON.stringify({ targetZipPath, sourcePaths }),
    });
  }

  public async unzipFile(id: string, zipPath: string, destinationPath: string): Promise<any> {
    return this.request(`/servers/${id}/files/unzip`, {
      method: 'POST',
      body: JSON.stringify({ zipPath, destinationPath }),
    });
  }

  public async uploadFile(id: string, targetDir: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', targetDir);

    const headers: Record<string, string> = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

    const res = await fetch(`${API_BASE}/servers/${id}/files/upload`, {
      method: 'POST',
      body: formData,
      headers,
      credentials: 'include',
    });

    const json = await res.json();
    if (!res.ok || !json.success) {
      throw new Error(json.error?.message || 'File upload failed');
    }
    return json.data;
  }

  public async copyEntry(id: string, sourcePath: string, destinationPath: string): Promise<any> {
    return this.request(`/servers/${id}/files/copy`, {
      method: 'POST',
      body: JSON.stringify({ sourcePath, destinationPath }),
    });
  }

  // Game Data (items.json, .hmap, .knav, .kcol)
  public async getGameData(id: string, level?: string): Promise<any> {
    return this.request(`/servers/${id}/game-data${level ? `?level=${encodeURIComponent(level)}` : ''}`);
  }

  public async uploadGameData(id: string, category: string, level: string | undefined, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    if (level) formData.append('level', level);

    const headers: Record<string, string> = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

    const res = await fetch(`${API_BASE}/servers/${id}/game-data/upload`, {
      method: 'POST',
      body: formData,
      headers,
      credentials: 'include',
    });

    const json = await res.json();
    if (!res.ok || !json.success) {
      throw new Error(json.error?.message || 'Game data upload failed');
    }
    return json.data;
  }

  // Gamemodes
  public async getAvailableGamemodes(id: string): Promise<{ activeScript: string; gamemodes: any[] }> {
    return this.request(`/servers/${id}/gamemodes`);
  }

  public async setActiveGamemode(id: string, script: string): Promise<any> {
    return this.request(`/servers/${id}/gamemodes/active`, {
      method: 'POST',
      body: JSON.stringify({ script }),
    });
  }

  // Config
  public async getConfig(id: string): Promise<{ rawToml: string; parsed: KcdServerTomlConfig }> {
    return this.request(`/servers/${id}/config`);
  }

  public async saveConfig(
    id: string,
    options: { rawToml?: string; config?: Record<string, unknown> }
  ): Promise<{ backup: string; requiresRestart: boolean }> {
    return this.request(`/servers/${id}/config`, {
      method: 'PUT',
      body: JSON.stringify(options),
    });
  }

  public async listConfigBackups(id: string): Promise<string[]> {
    return this.request(`/servers/${id}/config/backups`);
  }

  public async restoreConfigBackup(id: string, backupFilename: string): Promise<any> {
    return this.request(`/servers/${id}/config/restore`, {
      method: 'POST',
      body: JSON.stringify({ backupFilename }),
    });
  }

  // Gamemodes
  public async getGamemodes(id: string): Promise<any[]> {
    return this.request(`/servers/${id}/gamemodes`);
  }

  public async createGamemode(id: string, name: string, template: string): Promise<any> {
    return this.request(`/servers/${id}/gamemodes`, {
      method: 'POST',
      body: JSON.stringify({ name, template }),
    });
  }

  public async activateGamemode(id: string, name: string): Promise<any> {
    return this.request(`/servers/${id}/gamemodes/${encodeURIComponent(name)}/activate`, {
      method: 'POST',
    });
  }

  public async deleteGamemode(id: string, name: string): Promise<any> {
    return this.request(`/servers/${id}/gamemodes/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    });
  }

  // Players
  public async getPlayers(id: string): Promise<PlayerInfo[]> {
    return this.request<PlayerInfo[]>(`/servers/${id}/players`);
  }

  public async kickPlayer(id: string, playerId: string, reason?: string): Promise<any> {
    return this.request(`/servers/${id}/players/${playerId}/kick`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  public async banPlayer(id: string, playerId: string, reason?: string, durationMinutes?: number): Promise<any> {
    return this.request(`/servers/${id}/players/${playerId}/ban`, {
      method: 'POST',
      body: JSON.stringify({ reason, durationMinutes }),
    });
  }

  public async sendMessage(id: string, playerId: string, message: string): Promise<any> {
    return this.request(`/servers/${id}/players/${playerId}/message`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  }

  public async teleportPlayer(id: string, playerId: string, coords: { x: number; y: number; z: number }): Promise<any> {
    return this.request(`/servers/${id}/players/${playerId}/teleport`, {
      method: 'POST',
      body: JSON.stringify(coords),
    });
  }

  // Database Management
  public async getDatabaseInstances(id: string): Promise<DatabaseInstance[]> {
    return this.request<DatabaseInstance[]>(`/servers/${id}/database/instances`);
  }

  public async createDatabaseInstance(
    id: string,
    data: { name: string; type?: string }
  ): Promise<CreatedDatabaseCredentials> {
    return this.request<CreatedDatabaseCredentials>(`/servers/${id}/database/instances`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  public async deleteDatabaseInstance(id: string, dbId: string): Promise<{ deleted: boolean }> {
    return this.request<{ deleted: boolean }>(`/servers/${id}/database/instances/${dbId}`, {
      method: 'DELETE',
    });
  }

  public async resetDatabasePassword(id: string, dbId: string): Promise<{ newPassword: string }> {
    return this.request<{ newPassword: string }>(`/servers/${id}/database/instances/${dbId}/reset-password`, {
      method: 'POST',
    });
  }

  public async getDatabaseTables(id: string): Promise<DatabaseTableSummary[]> {
    return this.request<DatabaseTableSummary[]>(`/servers/${id}/database/tables`);
  }

  public async executeDatabaseQuery(id: string, query: string, limit: number = 50): Promise<DatabaseQueryResult> {
    return this.request<DatabaseQueryResult>(`/servers/${id}/database/query`, {
      method: 'POST',
      body: JSON.stringify({ query, limit }),
    });
  }

  // Backups
  public async getBackups(id: string): Promise<BackupInfo[]> {
    return this.request<BackupInfo[]>(`/servers/${id}/backups`);
  }

  public async createBackup(id: string, notes?: string): Promise<BackupInfo> {
    return this.request<BackupInfo>(`/servers/${id}/backups`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  public async restoreBackup(id: string, backupId: string): Promise<any> {
    return this.request(`/servers/${id}/backups/${backupId}/restore`, { method: 'POST' });
  }

  public async deleteBackup(id: string, backupId: string): Promise<any> {
    return this.request(`/servers/${id}/backups/${backupId}`, { method: 'DELETE' });
  }

  // Users
  public async getUsers(): Promise<any[]> {
    return this.request('/users');
  }

  public async createUser(data: any): Promise<any> {
    return this.request('/users', { method: 'POST', body: JSON.stringify(data) });
  }

  public async updateUser(id: string, data: any): Promise<any> {
    return this.request(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  public async deleteUser(id: string): Promise<any> {
    return this.request(`/users/${id}`, { method: 'DELETE' });
  }

  // Audit Logs
  public async getAuditLogs(
    params: { serverId?: string; userId?: string; action?: string; limit?: number; offset?: number } = {}
  ) {
    const q = new URLSearchParams();
    if (params.serverId) q.set('serverId', params.serverId);
    if (params.userId) q.set('userId', params.userId);
    if (params.action) q.set('action', params.action);
    if (params.limit) q.set('limit', String(params.limit));
    if (params.offset) q.set('offset', String(params.offset));
    return this.request<{ logs: AuditLogEntry[]; total: number }>(`/audit-logs?${q.toString()}`);
  }

  // API Keys
  public async getApiKeys(): Promise<any[]> {
    return this.request('/api-keys');
  }

  public async createApiKey(data: { name: string; expiresInDays?: number }): Promise<any> {
    return this.request('/api-keys', { method: 'POST', body: JSON.stringify(data) });
  }

  public async deleteApiKey(id: string): Promise<any> {
    return this.request(`/api-keys/${id}`, { method: 'DELETE' });
  }
}

export const api = new ApiClient();
